import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, gql } from '@apollo/client';

const TIPOS = [
  { value: 'text', label: 'Texto' },
  { value: 'textarea', label: 'Texto largo' },
  { value: 'date', label: 'Fecha' },
  { value: 'time', label: 'Hora' },
  { value: 'number', label: 'Número' },
];

const GET_PLANTILLAS = gql`
  query GetPlantillas {
    plantillas(order_by: { nombre: asc }) {
      id
      nombre
      plantilla
      plantilla_campos(order_by: { orden: asc }) {
        id
        key
        label
        type
        placeholder
        orden
      }
    }
  }
`;

const INSERT_PLANTILLA = gql`
  mutation InsertPlantilla($object: plantillas_insert_input!) {
    insert_plantillas_one(object: $object) {
      id
      nombre
      plantilla
    }
  }
`;

const UPDATE_PLANTILLA = gql`
  mutation UpdatePlantilla($id: uuid!, $_set: plantillas_set_input!) {
    update_plantillas_by_pk(pk_columns: { id: $id }, _set: $_set) {
      id
      nombre
      plantilla
    }
  }
`;

const DELETE_PLANTILLA = gql`
  mutation DeletePlantilla($id: uuid!) {
    delete_plantillas_by_pk(id: $id) {
      id
    }
  }
`;

const DELETE_CAMPOS = gql`
  mutation DeleteCampos($plantilla_id: uuid!) {
    delete_plantilla_campos(where: { plantilla_id: { _eq: $plantilla_id } }) {
      affected_rows
    }
  }
`;

const INSERT_CAMPOS = gql`
  mutation InsertCampos($objects: [plantilla_campos_insert_input!]!) {
    insert_plantilla_campos(objects: $objects) {
      affected_rows
    }
  }
`;

export default function GestionPlantillas({ onVolver }) {
  const [plantillas, setPlantillas] = useState([]);
  const [seleccion, setSeleccion] = useState('');
  const [nombre, setNombre] = useState('');
  const [plantilla, setPlantilla] = useState('');
  const [campos, setCampos] = useState([]);
  const [campo, setCampo] = useState({ key: '', label: '', type: 'text', placeholder: '' });

  const { data, loading, error, refetch } = useQuery(GET_PLANTILLAS);
  const [insertPlantilla] = useMutation(INSERT_PLANTILLA);
  const [updatePlantilla] = useMutation(UPDATE_PLANTILLA);
  const [deletePlantilla] = useMutation(DELETE_PLANTILLA);
  const [deleteCampos] = useMutation(DELETE_CAMPOS);
  const [insertCampos] = useMutation(INSERT_CAMPOS);

  useEffect(() => {
    setPlantillas(data?.plantillas || []);
  }, [data]);

  const actual = useMemo(() => plantillas.find(p => p.id === seleccion), [plantillas, seleccion]);

  useEffect(() => {
    if (actual) {
      setNombre(actual.nombre);
      setPlantilla(actual.plantilla);
      setCampos(actual.plantilla_campos || []);
    } else {
      setNombre('');
      setPlantilla('');
      setCampos([]);
    }
  }, [actual]);

  const limpiarFormulario = () => {
    setSeleccion('');
    setNombre('');
    setPlantilla('');
    setCampos([]);
  };

  const guardar = async () => {
    if (!nombre || !plantilla) return;
    if (!seleccion) {
      const creado = await insertPlantilla({ variables: { object: { nombre, plantilla } } });
      const nuevoId = creado.data?.insert_plantillas_one?.id;
      if (nuevoId && campos.length > 0) {
        await insertCampos({
          variables: {
            objects: campos.map((c, idx) => ({
              plantilla_id: nuevoId,
              key: c.key,
              label: c.label,
              type: c.type,
              placeholder: c.placeholder || null,
              orden: idx + 1,
            })),
          },
        });
      }
      await refetch();
      if (nuevoId) setSeleccion(nuevoId);
    } else {
      await updatePlantilla({ variables: { id: seleccion, _set: { nombre, plantilla } } });
      await deleteCampos({ variables: { plantilla_id: seleccion } });
      if (campos.length > 0) {
        await insertCampos({
          variables: {
            objects: campos.map((c, idx) => ({
              plantilla_id: seleccion,
              key: c.key,
              label: c.label,
              type: c.type,
              placeholder: c.placeholder || null,
              orden: idx + 1,
            })),
          },
        });
      }
      await refetch();
    }
  };

  const borrar = async () => {
    if (!seleccion) return;
    const ok = window.confirm('¿Eliminar plantilla? Esta acción no se puede deshacer.');
    if (!ok) return;
    await deletePlantilla({ variables: { id: seleccion } });
    await refetch();
    limpiarFormulario();
  };

  const agregarCampo = () => {
    if (!campo.key || !campo.label) return;
    if (campos.some(c => c.key === campo.key)) return alert('La clave del campo ya existe.');
    setCampos(prev => [...prev, campo]);
    setCampo({ key: '', label: '', type: 'text', placeholder: '' });
  };

  const eliminarCampo = (key) => {
    setCampos(prev => prev.filter(c => c.key !== key));
  };

  return (
    <div className="container py-4">
      {loading && (
        <div className="text-center mt-3">
          <div className="spinner-border text-primary"></div>
        </div>
      )}
      {error && (
        <div className="alert alert-danger small">No se pudieron cargar las plantillas.</div>
      )}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          {onVolver && (
            <button className="btn btn-outline-primary btn-volver" onClick={onVolver}>
              <i className="bi bi-arrow-left"></i>
              <span>Volver</span>
            </button>
          )}
          <h2 className="mb-0">Formatos y Formularios</h2>
        </div>
        <button className="btn btn-outline-secondary" onClick={limpiarFormulario}>Nuevo</button>
      </div>
      <div className="row g-4">
        <div className="col-12 col-lg-4">
          <div className="card h-100">
            <div className="card-header">Plantillas existentes</div>
            <div className="card-body">
              <ul className="list-group">
                {plantillas.map(p => (
                  <li key={p.id} className={`list-group-item ${seleccion === p.id ? 'active' : ''}`} style={{ cursor: 'pointer' }} onClick={() => setSeleccion(p.id)}>
                    {p.nombre}
                  </li>
                ))}
                {plantillas.length === 0 && <li className="list-group-item text-muted">Sin plantillas aún.</li>}
              </ul>
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-8">
          <div className="card">
            <div className="card-header">Editor de plantilla</div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label">Nombre</label>
                  <input className="form-control" value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Ej: Citación a Audiencia" />
                </div>
                <div className="col-12">
                  <label className="form-label">Contenido (usa {`{{llaves}}`} para variables)</label>
                  <textarea className="form-control" rows={6} value={plantilla} onChange={e => setPlantilla(e.target.value)} placeholder="Ej: Se cita a {{acusado}} el {{fecha}} a las {{hora}} en {{lugar}}." />
                </div>
                <div className="col-12">
                  <label className="form-label">Campos/Variables</label>
                  <div className="row g-2 align-items-end">
                    <div className="col-12 col-md-3">
                      <input className="form-control" placeholder={"Clave ({{clave}})"} value={campo.key} onChange={e => setCampo({ ...campo, key: e.target.value })} />
                    </div>
                    <div className="col-12 col-md-3">
                      <input className="form-control" placeholder="Etiqueta" value={campo.label} onChange={e => setCampo({ ...campo, label: e.target.value })} />
                    </div>
                    <div className="col-12 col-md-3">
                      <select className="form-select" value={campo.type} onChange={e => setCampo({ ...campo, type: e.target.value })}>
                        {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div className="col-12 col-md-3">
                      <input className="form-control" placeholder="Placeholder" value={campo.placeholder} onChange={e => setCampo({ ...campo, placeholder: e.target.value })} />
                    </div>
                    <div className="col-12 d-grid d-md-block">
                      <button className="btn btn-success" type="button" onClick={agregarCampo}>Agregar campo</button>
                    </div>
                  </div>
                  <ul className="list-group mt-3">
                    {campos.map(c => (
                      <li key={c.key} className="list-group-item d-flex justify-content-between align-items-center">
                        <div>
                          <span className="badge bg-secondary me-2">{`{{${c.key}}}`}</span>
                          {c.label} <span className="text-muted">({c.type})</span>
                          {c.placeholder && <span className="text-muted"> — {c.placeholder}</span>}
                        </div>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => eliminarCampo(c.key)}>Eliminar</button>
                      </li>
                    ))}
                    {campos.length === 0 && <li className="list-group-item text-muted">No hay campos definidos.</li>}
                  </ul>
                </div>
                <div className="col-12 d-flex gap-2">
                  <button className="btn btn-primary" onClick={guardar}>Guardar</button>
                  <button className="btn btn-outline-danger" onClick={borrar} disabled={!seleccion}>Eliminar</button>
                </div>
                <div className="col-12">
                  <div className="alert alert-info small">
                    Variables especiales disponibles: {`{{acusado}}`}, {`{{acusado_nombre}}`}, {`{{acusado_grado}}`}, {`{{acusado_unidad}}`}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
